import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { JOLLOF, getChef } from "@/lib/mockData";

export default function JollofProfile() {
  return (
    <div data-testid="jollof-page" className="bg-[#fbf9f7]">
      {/* Editorial hero */}
      <section className="relative min-h-[85vh] flex items-end grain">
        <img src={JOLLOF.hero} alt="Jollof" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-16 w-full">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="text-xs uppercase tracking-[0.35em] text-[#f5a623]">A Jollanda dish profile</span>
            <h1 className="font-display font-black text-white tracking-tighter leading-[0.85] text-7xl sm:text-8xl md:text-9xl mt-4">
              {JOLLOF.title}
            </h1>
            <p className="mt-6 text-xl text-neutral-200 max-w-2xl font-display italic">{JOLLOF.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {JOLLOF.facts.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="border-l-2 border-[#d00000] pl-4">
              <p className="text-xs uppercase tracking-[0.2em] text-neutral-400">{f.k}</p>
              <p className="text-neutral-900 mt-1 font-medium">{f.v}</p>
            </motion.div>
          ))}
        </div>
        <p className="font-display text-2xl sm:text-3xl leading-relaxed text-neutral-800">
          {JOLLOF.story}
        </p>
      </section>

      {/* Origins */}
      <section className="border-y border-neutral-200 bg-[#f4efec] py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">Regional rivalry</p>
          <h2 className="font-display text-4xl sm:text-5xl font-black text-neutral-900 mb-12">One dish, many nations.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {JOLLOF.origins.map((o, i) => (
              <motion.div key={o.region} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white border border-neutral-200 p-8" data-testid={`origin-${o.region.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Flame className="w-5 h-5" style={{ color: o.accent }} />
                  <h3 className="font-display text-2xl font-bold text-neutral-900">{o.region}</h3>
                </div>
                <p className="text-neutral-600 leading-relaxed">{o.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Versions from chefs */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">Taste the difference</p>
        <h2 className="font-display text-4xl sm:text-5xl font-black text-neutral-900 mb-12">Jollof, by our chefs.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {JOLLOF.versions.map((v, i) => {
            const chef = getChef(v.chef);
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Link to={`/chef/${v.chef}`} className="group block relative overflow-hidden aspect-[3/4] border border-neutral-200" data-testid={`jollof-version-${i}`}>
                  <img src={v.img} alt={v.style} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <p className="font-display text-2xl font-bold text-white">{v.style}</p>
                    <p className="text-sm text-neutral-300 mt-1">by {chef?.name}</p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="bg-[#d00000] p-12 text-center">
          <h3 className="font-display text-3xl sm:text-4xl font-black text-white">Pick a side. Order the plate.</h3>
          <p className="text-white/80 mt-3 max-w-lg mx-auto">The only way to win the jollof debate is to taste them all.</p>
          <Link to="/marketplace?cuisine=West%20African">
            <Button className="mt-8 bg-white text-[#d00000] hover:bg-neutral-100 rounded-none gap-2 h-12" data-testid="jollof-order-btn">
              Order jollof now <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
